// src/pages/SchoolSignup.js
import React, { useState } from "react";
import slugify from "slugify";
import { auth, db } from "../firebase";
import {
  createUserWithEmailAndPassword,
  updateProfile,
} from "firebase/auth";
import {
  doc,
  collection,
  writeBatch,
  Timestamp,
} from "firebase/firestore";

const DEFAULT_PBIS_VALUES = [
  "Respect",
  "Responsibility",
  "Kindness",
  "Communication",
];

const SignupPage = () => {
  const [schoolName, setSchoolName] = useState("");
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    let cred;
    let schoolSetupComplete = false;

    try {
      const trimmedSchoolName = schoolName.trim();
      const trimmedAdminName = adminName.trim();
      const trimmedAdminEmail = adminEmail.trim().toLowerCase();

      if (!trimmedSchoolName || !trimmedAdminName || !trimmedAdminEmail || !adminPassword) {
        throw new Error("Please fill out all fields.");
      }

      if (adminPassword.length < 6) {
        throw new Error("Password must be at least 6 characters.");
      }

      const schoolId = slugify(trimmedSchoolName, {
        lower: true,
        strict: true,
      });

      if (!schoolId) {
        throw new Error("Please enter a valid school name.");
      }

      const schoolRef = doc(db, "schools", schoolId);

      cred = await createUserWithEmailAndPassword(
        auth,
        trimmedAdminEmail,
        adminPassword
      );

      const uid = cred.user.uid;

      await updateProfile(cred.user, {
        displayName: trimmedAdminName,
      });

      const batch = writeBatch(db);

      batch.set(schoolRef, {
        id: schoolId,
        name: trimmedSchoolName,
        createdBy: uid,
        createdAt: Timestamp.now(),
        primaryAdminName: trimmedAdminName,
        primaryAdminEmail: trimmedAdminEmail,
        plan: "founding",
        paymentStatus: "pending",
        status: "active",
      });

      batch.set(doc(db, "users", uid), {
        uid,
        id: uid,
        name: trimmedAdminName,
        email: trimmedAdminEmail,
        role: "admin",
        schoolId,
        class: "Unassigned",
        advisory: "Unassigned",
        createdAt: Timestamp.now(),
        ticketBalance: 0,
        tickets: 0,
      });

      batch.set(doc(db, "schools", schoolId, "totals", "classes"), {
        list: ["Unassigned"],
      });

      batch.set(doc(db, "schools", schoolId, "totals", "classLabels"), {
        Unassigned: "Unassigned",
      });

      batch.set(doc(db, "schools", schoolId, "totals", "yearly"), {
        totalTicketsOverride: 0,
        advisoryOverrides: {},
      });

      await batch.commit();

      const pbisBatch = writeBatch(db);
      DEFAULT_PBIS_VALUES.forEach((value) => {
        const valueRef = doc(collection(db, "schools", schoolId, "pbisValues"));
        pbisBatch.set(valueRef, {
          name: value,
          createdAt: Timestamp.now(),
        });
      });
      await pbisBatch.commit();
      schoolSetupComplete = true;

      const idToken = await cred.user.getIdToken();

      const response = await fetch(
        "https://us-central1-pbis-app-72ba5.cloudfunctions.net/createCheckoutSession",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            schoolId,
            email: trimmedAdminEmail,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.url) {
        throw new Error(data.error || "Failed to create Stripe checkout session.");
      }

      window.location.href = data.url;
    } catch (err) {
      console.error("School signup error:", err);
    
      // Remove Auth only when Firestore setup never completed. Once the school
      // exists, preserve the account so the administrator can retry payment.
      if (cred?.user && !schoolSetupComplete) {
        try {
          await cred.user.delete();
        } catch (deleteErr) {
          console.error("Failed to delete partial user:", deleteErr);
        }
      }
    
      setError(
        schoolSetupComplete
          ? "Your school account was created, but checkout could not start. Sign in to retry payment."
          : err.code === "permission-denied"
            ? "That school name is already in use. Please choose another name."
            : err.message || "Failed to create school account."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        maxWidth: "700px",
        margin: "3rem auto",
        padding: "2rem",
        backgroundColor: "#ffffff",
        borderRadius: "14px",
        boxShadow: "0 6px 22px rgba(0,0,0,0.08)",
        fontFamily: "'Segoe UI', sans-serif",
      }}
    >
      <div style={{ textAlign: "center", marginBottom: "1.75rem" }}>
        <h1 style={{ marginBottom: "0.5rem", color: "#1e293b" }}>
          Create Your School Account
        </h1>
        <p style={{ margin: 0, color: "#475569", lineHeight: "1.6" }}>
          Set up a new PBIS Portal school space and create the first admin account.
        </p>
      </div>

      <form onSubmit={handleSignup} style={{ display: "grid", gap: "1rem" }}>
        <div>
          <label htmlFor="schoolName" style={labelStyle}>School Name</label>
          <input
            id="schoolName"
            type="text"
            value={schoolName}
            onChange={(e) => setSchoolName(e.target.value)}
            required
            placeholder="Example: Northern Lights Community School"
            style={inputStyle}
          />
        </div>

        <div>
          <label htmlFor="adminName" style={labelStyle}>Admin Name</label>
          <input
            id="adminName"
            type="text"
            value={adminName}
            onChange={(e) => setAdminName(e.target.value)}
            required
            placeholder="Your full name"
            style={inputStyle}
          />
        </div>

        <div>
          <label htmlFor="adminEmail" style={labelStyle}>Admin Email</label>
          <input
            id="adminEmail"
            type="email"
            value={adminEmail}
            onChange={(e) => setAdminEmail(e.target.value)}
            required
            placeholder="you@school.org"
            style={inputStyle}
          />
        </div>

        <div>
          <label htmlFor="adminPassword" style={labelStyle}>Password</label>
          <input
            id="adminPassword"
            type="password"
            value={adminPassword}
            onChange={(e) => setAdminPassword(e.target.value)}
            required
            minLength={6}
            placeholder="At least 6 characters"
            style={inputStyle}
          />
        </div>

        <div
  style={{
    backgroundColor: "#ecfdf5",
    border: "1px solid #bbf7d0",
    borderRadius: "10px",
    padding: "1rem",
    color: "#065f46",
    fontSize: "0.95rem",
    lineHeight: "1.6",
  }}
>
  <strong>PBIS Portal Annual Plan</strong>
  <br />
  Get full access to PBIS Portal for your school for <strong>$150/year</strong>.
  <br />
  <span style={{ opacity: 0.85 }}>
    Your subscription renews annually until cancelled.
  </span>
</div>
        <button
          type="submit"
          disabled={loading}
          style={{
            backgroundColor: loading ? "#94a3b8" : "#10b981",
            color: "#fff",
            border: "none",
            padding: "0.85rem 1rem",
            borderRadius: "8px",
            fontWeight: 700,
            fontSize: "1rem",
            cursor: loading ? "not-allowed" : "pointer",
            marginTop: "0.25rem",
          }}
        >
          {loading ? "Creating School..." : "Create School Account"}
        </button>

        {error && (
          <p
            style={{
              color: "#b91c1c",
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "8px",
              padding: "0.75rem",
              margin: 0,
            }}
          >
            {error}
          </p>
        )}
      </form>
    </div>
  );
};

const labelStyle = {
  display: "block",
  marginBottom: "0.35rem",
  fontWeight: 600,
};

const inputStyle = {
  width: "100%",
  padding: "0.75rem",
  borderRadius: "8px",
  border: "1px solid #cbd5e1",
  fontSize: "1rem",
  boxSizing: "border-box",
  backgroundColor: "#fff",
};

export default SignupPage;
